import { pushContent } from '@bb/contracts';
import { ApiError, type PushContentInput, type PushContentIssue, type PushValidationResult } from '../../../api/client.ts';
import { humanizePath } from '../shared/localization.ts';
import { PLATFORM_LABELS, type PushPlatform } from './pushDraft.ts';

/**
 * Checks of the push composer (BB-73, BB-76): the contract's structure first (in the browser, so a half-typed link is
 * explained without a round trip), then `POST …/push/validate` — templates, texts, lengths, payload size, send
 * settings against the environment registry. Issues carry the path of their field, so each field shows its own.
 */

/** Keys of the payload the SDK and the providers own (`@bb/domain` `RESERVED_EXTRA_KEYS`). */
export const RESERVED_EXTRA_KEYS = ['bb', 'aps', 'ab', '_ab', 'from', 'notification', 'message_type', 'collapse_key'] as const;
export const COLLAPSE_KEY_MAX_BYTES = 64;
export const PAYLOAD_MAX_BYTES = 4096;

export const isReservedExtraKey = (key: string) => (RESERVED_EXTRA_KEYS as readonly string[]).includes(key);
export const hasLiquid = (text: string) => text.includes('{{') || text.includes('{%') || text.includes('${');
export const utf8Bytes = (text: string) => new TextEncoder().encode(text).length;

/** A zod path as the server writes template paths: `androidButtons[0].label`. */
export const issuePath = (path: ReadonlyArray<PropertyKey>): string =>
  path.reduce<string>((out, seg) => (typeof seg === 'number' ? `${out}[${seg}]` : out ? `${out}.${String(seg)}` : String(seg)), '');

function structuralMessage(issue: { code: string; message: string; minimum?: unknown; maximum?: unknown; origin?: unknown }): string {
  if (issue.code === 'too_small' && issue.origin === 'string' && issue.minimum === 1) return 'Required.';
  if (issue.code === 'too_big' && issue.origin === 'string') return `At most ${String(issue.maximum)} characters.`;
  if (issue.code === 'too_big' && issue.origin === 'array') return `At most ${String(issue.maximum)}.`;
  return issue.message.endsWith('.') ? issue.message : `${issue.message}.`;
}

/** Structural problems the contract finds (empty link, id format, TTL range…), as errors with paths. */
export function structuralIssues(content: PushContentInput): PushContentIssue[] {
  const parsed = pushContent.PushContent.safeParse(content);
  if (parsed.success) return [];
  return parsed.error.issues.map((i) => ({ code: 'invalid', path: issuePath(i.path), message: structuralMessage(i as unknown as Parameters<typeof structuralMessage>[0]) }));
}

/** A 400 `validation_failed` of the API, as issues. */
export function problemIssues(error: unknown): PushContentIssue[] | null {
  if (!(error instanceof ApiError) || error.code !== 'validation_failed') return null;
  const issues = Array.isArray(error.problem.issues) ? (error.problem.issues as Array<{ path?: string; message?: string }>) : [];
  return issues.map((i) => ({ code: 'invalid', path: (i.path ?? '').replace(/^content\.?/, ''), message: i.message ?? 'Invalid.' }));
}

export const emptyResult = (errors: PushContentIssue[] = []): PushValidationResult => ({ errors, warnings: [], lengths: [], estimatedPayloadBytes: {} });

/** Results of several contents (separate mode) as one, each issue with its platform. */
export function mergeResults(results: ReadonlyArray<{ platforms: readonly PushPlatform[]; result: PushValidationResult }>): PushValidationResult {
  const tag = (issues: PushContentIssue[], platforms: readonly PushPlatform[]) => issues.map((i) => (i.platform || platforms.length !== 1 ? i : { ...i, platform: platforms[0] }));
  return {
    errors: results.flatMap((r) => tag(r.result.errors, r.platforms)),
    warnings: results.flatMap((r) => tag(r.result.warnings, r.platforms)),
    lengths: results.flatMap((r) => r.result.lengths),
    estimatedPayloadBytes: Object.assign({}, ...results.map((r) => r.result.estimatedPayloadBytes)),
  };
}

export interface FieldIssues {
  errors: PushContentIssue[];
  warnings: PushContentIssue[];
}

/** Looks up the issues of a field; `platform` keeps another platform's issues off a separate-mode tab. */
export type IssueLookup = (path: string, options?: { prefix?: boolean }) => FieldIssues;

export function issueLookup(result: PushValidationResult | undefined, platforms?: readonly PushPlatform[]): IssueLookup {
  return (path, options = {}) => {
    const matches = (i: PushContentIssue) =>
      (options.prefix ? i.path === path || i.path.startsWith(`${path}.`) || i.path.startsWith(`${path}[`) : i.path === path) && (!platforms || !i.platform || platforms.includes(i.platform));
    return { errors: result?.errors.filter(matches) ?? [], warnings: result?.warnings.filter(matches) ?? [] };
  };
}

export const FIELD_LABELS: Readonly<Record<string, string>> = {
  title: 'Title',
  body: 'Message',
  'androidOverrides.title': 'Android title',
  'androidOverrides.body': 'Android message',
  'androidOverrides.summary': 'Summary text',
  'iosOverrides.title': 'iOS title',
  'iosOverrides.subtitle': 'Subtitle',
  'iosOverrides.body': 'iOS message',
  'action.uri': 'Click action link',
  'media.imageUrl': 'Image URL',
  androidButtons: 'Android buttons',
  'settings.collapseKey': 'Collapse key',
  'settings.androidPriority': 'Delivery priority',
  'settings.androidChannelId': 'Notification channel',
  'settings.iosInterruptionLevel': 'Interruption level',
  'settings.ttl.seconds': 'Time to live',
  'settings.iosRelevanceScore': 'Relevance score',
  'settings.androidNotificationId.fixed': 'Notification ID',
  'settings.iosBadge.value': 'Badge',
};

/** Where an issue is, in words: «pt-BR · Title», «Key-value pair "bb"», «Payload». */
export function issueLocation(issue: PushContentIssue): string {
  const platform = issue.platform ? `${PLATFORM_LABELS[issue.platform]} · ` : '';
  if (!issue.path) return `${platform}Payload`;
  const localized = /^localized\.([^.]+)\.(.+)$/.exec(issue.path);
  if (localized) return `${platform}${localized[1]} · ${FIELD_LABELS[localized[2]!] ?? humanizePath(localized[2]!)}`;
  const extra = /^extras\.(.+)$/.exec(issue.path);
  if (extra) return `${platform}Key-value pair "${extra[1]}"`;
  return `${platform}${FIELD_LABELS[issue.path] ?? humanizePath(issue.path.replace(/\[(\d+)\]/g, '.$1'))}`;
}

/** `2419200` → «28 days», `3600` → «1 hour», `90` → «90 seconds». */
export function formatDuration(seconds: number): string {
  const units: Array<[number, string]> = [
    [86_400, 'day'],
    [3_600, 'hour'],
    [60, 'minute'],
  ];
  for (const [size, name] of units) {
    if (seconds >= size && seconds % size === 0) {
      const n = seconds / size;
      return `${n} ${name}${n === 1 ? '' : 's'}`;
    }
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`;
}
